/** Shared helpers for resolving which trip record holds GPS data. */

export function resolveAlternateGpsTripId(tripId, tripPayload, routePayload) {
  const fromRoute = routePayload?.resolved_from_trip_id;
  const fromTrip = tripPayload?.gps_sibling_trip_id;
  const resolved = fromRoute || fromTrip;
  if (resolved && String(resolved) !== String(tripId)) {
    return parseInt(resolved, 10);
  }
  return null;
}

export function routeHasGpsData(route) {
  if (!route) return false;
  if (route.route_source === 'waypoints') return true;
  if ((route.track_count || 0) > 0) return true;
  if (Array.isArray(route.track) && route.track.length > 0) return true;
  if (Array.isArray(route.points) && route.points.length > 0) return true;
  return false;
}

/** Normalize route polyline to [[lat, lon], ...] from tuples or {latitude, longitude} rows. */
export function normalizeMapTrack(track) {
  if (!Array.isArray(track)) return [];
  const out = [];
  for (const point of track) {
    if (Array.isArray(point) && point.length >= 2) {
      const lat = parseFloat(point[0]);
      const lon = parseFloat(point[1]);
      if (Number.isFinite(lat) && Number.isFinite(lon)) out.push([lat, lon]);
      continue;
    }
    if (point && typeof point === 'object') {
      const lat = parseFloat(point.latitude ?? point.lat);
      const lon = parseFloat(point.longitude ?? point.lon ?? point.lng);
      if (Number.isFinite(lat) && Number.isFinite(lon)) out.push([lat, lon]);
    }
  }
  return out;
}

/** Prefer GPS polyline; fall back to deduplicated waypoint stops for map + stats. */
export function resolveEffectiveTripTrack({ gpsTrack = [], waypoints = [] } = {}) {
  const fromGps = normalizeMapTrack(gpsTrack);
  if (fromGps.length > 1) {
    return { track: fromGps, source: 'tracks' };
  }
  const fromWaypoints = waypointsToMapTrack(waypoints);
  if (fromWaypoints.length > 1) {
    return { track: fromWaypoints, source: 'waypoints' };
  }
  if (fromGps.length === 1) {
    return { track: fromGps, source: 'tracks' };
  }
  return { track: [], source: null };
}

/** Sum short gaps between consecutive anchor stops (moving legs only). */
export function computeWaypointUnderwayMinutes(waypoints) {
  if (!Array.isArray(waypoints) || waypoints.length < 2) return 0;
  const sorted = [...waypoints]
    .filter((wp) => wp && (wp.arrival_time || wp.departure_time || wp.created_at))
    .sort((a, b) => {
      const ta = new Date(a.arrival_time || a.created_at || 0).getTime();
      const tb = new Date(b.arrival_time || b.created_at || 0).getTime();
      return ta - tb || (a.id || 0) - (b.id || 0);
    });
  if (sorted.length < 2) return 0;

  const maxLegMs = 24 * 60 * 60 * 1000;
  let totalMs = 0;
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const leave = sorted[i].departure_time || sorted[i].arrival_time || sorted[i].created_at;
    const arrive = sorted[i + 1].arrival_time || sorted[i + 1].created_at;
    if (!leave || !arrive) continue;
    const t0 = new Date(leave).getTime();
    const t1 = new Date(arrive).getTime();
    if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) continue;
    const gap = t1 - t0;
    if (gap > 0 && gap <= maxLegMs) totalMs += gap;
  }
  return Math.max(0, Math.round(totalMs / 60000));
}

/** Build map polyline [[lat,lon],...] from waypoint rows. */
export function waypointsToMapTrack(waypoints, maxPoints = 2500) {
  if (!Array.isArray(waypoints) || waypoints.length === 0) return [];
  const sorted = [...waypoints]
    .filter((wp) => Number.isFinite(parseFloat(wp.latitude)) && Number.isFinite(parseFloat(wp.longitude)))
    .sort((a, b) => {
      const ta = new Date(a.arrival_time || a.created_at || 0).getTime();
      const tb = new Date(b.arrival_time || b.created_at || 0).getTime();
      return ta - tb || (a.id || 0) - (b.id || 0);
    });
  if (sorted.length <= maxPoints) {
    return sorted.map((wp) => [parseFloat(wp.latitude), parseFloat(wp.longitude)]);
  }
  const step = Math.ceil(sorted.length / maxPoints);
  const out = [];
  for (let i = 0; i < sorted.length; i += step) {
    out.push([parseFloat(sorted[i].latitude), parseFloat(sorted[i].longitude)]);
  }
  const last = sorted[sorted.length - 1];
  const tail = [parseFloat(last.latitude), parseFloat(last.longitude)];
  if (out[out.length - 1][0] !== tail[0] || out[out.length - 1][1] !== tail[1]) out.push(tail);
  return out;
}
